"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PasswordPolicy = exports.ConfirmResetPasswordPayload = exports.ResetPasswordPayload = exports.Profile = exports.Avatar = exports.Container = exports.ContainerPreferences = exports.GoalsSummary = exports.HydrationHistory = exports.HydrationStatus = exports.HydrationGoals = exports.ProfileQuery = exports.UpdateAccountPayload = exports.CreateAccountPayload = exports.NAMESPACE = void 0;
const effect_1 = require("effect");
const field_1 = require("../../../openapi/field");
exports.NAMESPACE = 'Culligan.Api.V1.Frontend.Account.Schema';
const emailFormat = (description) => effect_1.Schema.propertySignature(effect_1.Schema.String.annotations({ jsonSchema: { type: 'string', format: 'email' } })).annotations({ description });
const optionalDateTime = (description) => effect_1.Schema.optionalWith((0, field_1.dateTime)(description), { exact: true }).annotations({ description });
const passwordPolicy = 'Must have at least 8 characters, with at least 1 lowercase letter, 1 uppercase letter, 1 number and 1 symbol (the same policy GET /user/passwordpolicy returns).';
const passwordDescription = `Password. ${passwordPolicy}`;
const emailDescription = 'Email address. The server lower-cases it before use.';
const emptyAsNull = 'An empty string is stored as null.';
exports.CreateAccountPayload = effect_1.Schema.Struct({
    email: emailFormat(emailDescription),
    password: effect_1.Schema.propertySignature(effect_1.Schema.NonEmptyString).annotations({ description: passwordDescription }),
    firstName: (0, field_1.nonEmpty)('First name'),
    lastName: (0, field_1.nonEmpty)('Last name'),
    country: (0, field_1.nonEmpty)('Country of residence'),
    countryCode: (0, field_1.optionalString)(`ISO 3166 country code. ${emptyAsNull}`),
    mobilePhoneNumber: (0, field_1.optionalString)(`Mobile phone number. ${emptyAsNull}`),
    address: (0, field_1.optionalString)(`Street address. ${emptyAsNull}`),
    addressExtra: (0, field_1.optionalString)(`Extra address information. ${emptyAsNull}`),
    city: (0, field_1.optionalString)(`City. ${emptyAsNull}`),
    zip: (0, field_1.optionalString)(`ZIP / postal code. ${emptyAsNull}`),
    state: (0, field_1.optionalString)(`State / province. ${emptyAsNull}`),
    dealerId: (0, field_1.optionalString)('Reference dealer id. Not validated; stored as sent.'),
    tenantId: (0, field_1.optionalString)('Tenant id. Not validated; stored as sent.'),
    appId: (0, field_1.nonEmpty)('Client application id. Must belong to a registered, enabled client application, otherwise the request is rejected with 422.'),
}).annotations({
    identifier: `${exports.NAMESPACE}.CreateAccountPayload`,
    description: 'Request body to create a user account',
});
exports.UpdateAccountPayload = effect_1.Schema.Struct({
    email: (0, field_1.optionalEmail)(`${emailDescription} Changes the login email.`),
    password: effect_1.Schema.optionalWith(effect_1.Schema.NonEmptyString, { exact: true }).annotations({
        description: passwordDescription,
    }),
    firstName: (0, field_1.optionalString)(`First name. ${emptyAsNull}`),
    lastName: (0, field_1.optionalString)(`Last name. ${emptyAsNull}`),
    country: (0, field_1.optionalString)(`Country of residence. ${emptyAsNull}`),
    countryCode: (0, field_1.optionalString)(`ISO 3166 country code. ${emptyAsNull}`),
    mobilePhoneNumber: (0, field_1.optionalString)(`Mobile phone number. ${emptyAsNull}`),
    address: (0, field_1.optionalString)(`Street address. ${emptyAsNull}`),
    addressExtra: (0, field_1.optionalString)(`Extra address information. ${emptyAsNull}`),
    city: (0, field_1.optionalString)(`City. ${emptyAsNull}`),
    zip: (0, field_1.optionalString)(`ZIP / postal code. ${emptyAsNull}`),
    state: (0, field_1.optionalString)(`State / province. ${emptyAsNull}`),
    dealerId: (0, field_1.optionalString)('Reference dealer id. Not validated; stored as sent.'),
    tenantId: (0, field_1.optionalString)('Tenant id. Not validated; stored as sent.'),
}).annotations({
    identifier: `${exports.NAMESPACE}.UpdateAccountPayload`,
    description: 'Request body to update the signed-in user account (PUT and PATCH)',
});
exports.ProfileQuery = effect_1.Schema.Struct({
    type: effect_1.Schema.optionalWith(effect_1.Schema.Literal('basic', 'full'), { exact: true }).annotations({
        description: 'basic (default) returns the stored profile fields. full also adds the dashboard fields: hydration goals, status and history, saved bottles, containers, avatar and accent color.',
    }),
}).annotations({
    identifier: `${exports.NAMESPACE}.ProfileQuery`,
    description: 'Query string of the profile request',
});
exports.HydrationGoals = effect_1.Schema.Struct({
    day: effect_1.Schema.optionalWith(effect_1.Schema.Number, { exact: true }).annotations({
        description: 'Daily goal in milliliters. Absent when the user has not set a goal.',
    }),
}).annotations({
    identifier: `${exports.NAMESPACE}.HydrationGoals`,
    description: 'Hydration goals of the user; an empty object when none are set',
});
exports.HydrationStatus = effect_1.Schema.Struct({
    level: effect_1.Schema.propertySignature(effect_1.Schema.Number).annotations({ description: 'Milliliters dispensed today' }),
    bottlesToGoal: effect_1.Schema.propertySignature(effect_1.Schema.Number).annotations({
        description: 'Daily goal divided by 500 ml, minus the number of refills today (each refill counts as one bottle, whatever its size); never below 0, 0 without a goal',
    }),
    glassesToGoal: effect_1.Schema.propertySignature(effect_1.Schema.Number).annotations({
        description: 'Glasses (200 ml) left to reach the daily goal, never below 0; 0 without a goal',
    }),
}).annotations({
    identifier: `${exports.NAMESPACE}.HydrationStatus`,
    description: 'Hydration progress of the user today',
});
exports.HydrationHistory = effect_1.Schema.Struct({
    today: effect_1.Schema.propertySignature(effect_1.Schema.Record({ key: effect_1.Schema.String, value: effect_1.Schema.Number })).annotations({
        description: 'Milliliters dispensed per hour of the day. Always has the 24 keys "0" to "23". The hour is read in the server time zone, while "today" starts at midnight of the user time zone offset.',
    }),
}).annotations({
    identifier: `${exports.NAMESPACE}.HydrationHistory`,
    description: 'Hydration history of the user',
});
exports.GoalsSummary = effect_1.Schema.Struct({
    savedBottles: effect_1.Schema.propertySignature(effect_1.Schema.Int).annotations({
        description: 'Number of refills made today',
    }),
}).annotations({
    identifier: `${exports.NAMESPACE}.GoalsSummary`,
    description: 'Summary of the hydration goals',
});
exports.ContainerPreferences = effect_1.Schema.Struct({
    waterType: effect_1.Schema.optionalWith(effect_1.Schema.Number, { exact: true }).annotations({
        description: 'Water type code',
    }),
}).annotations({
    identifier: `${exports.NAMESPACE}.ContainerPreferences`,
    description: 'Content preferences stored with a container',
});
exports.Container = effect_1.Schema.Struct({
    id: (0, field_1.nonEmpty)('Container id (the NFC tag id when there is one)'),
    userId: (0, field_1.nonEmpty)('Owner user id'),
    name: effect_1.Schema.optionalWith(effect_1.Schema.String, { exact: true }).annotations({ description: 'Container name' }),
    size: effect_1.Schema.optionalWith(effect_1.Schema.Number, { exact: true }).annotations({
        description: 'Container size in milliliters',
    }),
    tagId: effect_1.Schema.optionalWith(effect_1.Schema.NullOr(effect_1.Schema.String), { exact: true }).annotations({
        description: 'NFC tag id, or null',
    }),
    preferences: effect_1.Schema.optionalWith(effect_1.Schema.NullOr(exports.ContainerPreferences), { exact: true }).annotations({
        description: 'Content preferences, or null',
    }),
    waterType: effect_1.Schema.optionalWith(effect_1.Schema.Number, { exact: true }).annotations({
        description: 'Copy of preferences.waterType, added at the top level when preferences are set',
    }),
    createdAt: optionalDateTime('ISO-8601 creation time'),
    updatedAt: optionalDateTime('ISO-8601 last update time'),
}).annotations({
    identifier: `${exports.NAMESPACE}.Container`,
    description: 'Container of the user',
});
exports.Avatar = effect_1.Schema.Struct({
    content: effect_1.Schema.propertySignature(effect_1.Schema.NullOr(effect_1.Schema.String)).annotations({
        description: 'Always null: avatars are not served',
    }),
}).annotations({
    identifier: `${exports.NAMESPACE}.Avatar`,
    description: 'Avatar of the user',
});
const fromDashboard = 'Filled from the dashboard for type=full, unless the stored profile has this field: an account created or replaced through this API stores null here, and the stored null wins, so null can come back for type=basic and type=full alike.';
/** Stored as null by create and PUT, which copy every profile field, sent or not. */
const dashboardField = (schema, description) => effect_1.Schema.optionalWith(effect_1.Schema.NullOr(schema), { exact: true }).annotations({
    description: `${description}. ${fromDashboard}`,
});
exports.Profile = effect_1.Schema.Struct({
    userId: (0, field_1.nonEmpty)('User id'),
    email: effect_1.Schema.optionalWith(effect_1.Schema.NullOr(effect_1.Schema.String), { exact: true }).annotations({
        description: 'Email address',
    }),
    firstName: (0, field_1.optionalNullableString)('First name'),
    lastName: (0, field_1.optionalNullableString)('Last name'),
    country: (0, field_1.optionalNullableString)('Country of residence'),
    countryCode: (0, field_1.optionalNullableString)('ISO 3166 country code'),
    mobilePhoneNumber: (0, field_1.optionalNullableString)('Mobile phone number'),
    address: (0, field_1.optionalNullableString)('Street address'),
    addressExtra: (0, field_1.optionalNullableString)('Extra address information'),
    city: (0, field_1.optionalNullableString)('City'),
    zip: (0, field_1.optionalNullableString)('ZIP / postal code'),
    state: (0, field_1.optionalNullableString)('State / province'),
    dealerId: (0, field_1.optionalNullableString)('Reference dealer id'),
    tenantId: (0, field_1.nullableString)('Tenant id, or null when the user has none'),
    hydrationGoals: dashboardField(exports.HydrationGoals, 'Hydration goals'),
    hydrationStatus: dashboardField(exports.HydrationStatus, 'Hydration progress today'),
    hydrationHistory: dashboardField(exports.HydrationHistory, 'Hydration history'),
    goalsSummary: dashboardField(exports.GoalsSummary, 'Summary of the hydration goals'),
    containers: dashboardField(effect_1.Schema.Array(exports.Container), 'Containers of the user'),
    avatar: dashboardField(exports.Avatar, 'Avatar'),
    accentColor: dashboardField(effect_1.Schema.String, 'Accent color, null when none is set'),
}).annotations({
    identifier: `${exports.NAMESPACE}.Profile`,
    description: 'Profile of the signed-in user',
});
exports.ResetPasswordPayload = effect_1.Schema.Struct({
    email: emailFormat(emailDescription),
    lang: effect_1.Schema.optionalWith(effect_1.Schema.String.pipe(effect_1.Schema.length(2)), { exact: true }).annotations({
        description: 'Two-letter language code of the email (any two characters, not checked against a language list). Defaults to "en" when omitted.',
    }),
    appId: (0, field_1.nonEmpty)('Client application id. Must belong to a registered, enabled client application, otherwise the request is rejected with 422.'),
}).annotations({
    identifier: `${exports.NAMESPACE}.ResetPasswordPayload`,
    description: 'Request body to send a password reset email',
});
exports.ConfirmResetPasswordPayload = effect_1.Schema.Struct({
    password: effect_1.Schema.propertySignature(effect_1.Schema.NonEmptyString).annotations({
        description: `New password. ${passwordPolicy}`,
    }),
    oobCode: (0, field_1.nonEmpty)('One-time code from the password reset link sent by email'),
}).annotations({
    identifier: `${exports.NAMESPACE}.ConfirmResetPasswordPayload`,
    description: 'Request body to set a new password with the code from the reset email',
});
exports.PasswordPolicy = effect_1.Schema.Struct({
    minLength: effect_1.Schema.propertySignature(effect_1.Schema.Int).annotations({ description: 'Minimum length' }),
    minLowercase: effect_1.Schema.propertySignature(effect_1.Schema.Int).annotations({
        description: 'Minimum number of lowercase letters',
    }),
    minUppercase: effect_1.Schema.propertySignature(effect_1.Schema.Int).annotations({
        description: 'Minimum number of uppercase letters',
    }),
    minNumbers: effect_1.Schema.propertySignature(effect_1.Schema.Int).annotations({ description: 'Minimum number of digits' }),
    minSymbols: effect_1.Schema.propertySignature(effect_1.Schema.Int).annotations({
        description: 'Minimum number of symbols',
    }),
}).annotations({
    identifier: `${exports.NAMESPACE}.PasswordPolicy`,
    description: 'Password policy for user accounts',
});
//# sourceMappingURL=schema.js.map